import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CatalogService, CatalogWarehouse } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-warehouses',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('catalog.warehouses.title')" [description]="t('catalog.warehouses.description')">
      <ain-inventory-page-hint pageKey="warehouses" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreateForm()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="t('common.export')" severity="contrast" [disabled]="rows().length === 0"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('catalog.filter.title') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.name" (ngModelChange)="applyFilters()" />
              </label>
              <label>
                <span>{{ t('catalog.filter.code') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.code" (ngModelChange)="applyFilters()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('columns.code') }}</th>
                <th>{{ t('columns.title') }}</th>
                <th>{{ t('catalog.warehouses.active') }}</th>
                <th>{{ t('catalog.warehouses.organizationUnitId') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.organizationUnitId || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class InventoryWarehousesComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly showFilters = signal(true);
  readonly sourceRows = signal<CatalogWarehouse[]>([]);
  readonly rows = signal<CatalogWarehouse[]>([]);
  readonly selectedRows = signal<CatalogWarehouse[]>([]);
  readonly filters = { name: '', code: '' };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listWarehouses().subscribe({
      next: warehouses => {
        this.sourceRows.set(warehouses);
        this.applyFilters();
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.warehouses.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  onSelectionChange(value: CatalogWarehouse[]): void {
    this.selectedRows.set(value ?? []);
  }

  applyFilters(): void {
    const name = this.filters.name.trim().toLowerCase();
    const code = this.filters.code.trim().toLowerCase();
    const filtered = this.sourceRows().filter(item =>
      (!name || item.name.toLowerCase().includes(name))
      && (!code || item.code.toLowerCase().includes(code)));
    this.rows.set(filtered);
    const selectedIds = new Set(this.selectedRows().map(item => item.id));
    this.selectedRows.set(filtered.filter(item => selectedIds.has(item.id)));
  }

  openCreateForm(): void {
    void this.router.navigate(['/inventory/warehouses', 'new']);
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows();
    if (selected.length === 1) {
      this.openDetail(selected[0].id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/warehouses', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
